import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Fire, Medal, ChefHat, Snowflake } from "@phosphor-icons/react";
import api from "@/lib/api";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "all", label: "All-Time", icon: Trophy },
  { id: "month", label: "This Month", icon: Medal },
  { id: "week", label: "This Week", icon: Fire },
];

const RANK_ACCENT = {
  1: { medal: "🥇", ring: "ring-rf-amber", bg: "bg-gradient-to-br from-rf-amber/40 to-rf-orange/20" },
  2: { medal: "🥈", ring: "ring-slate-300", bg: "bg-gradient-to-br from-slate-200 to-slate-100" },
  3: { medal: "🥉", ring: "ring-orange-300", bg: "bg-gradient-to-br from-orange-200 to-orange-100" },
};

function PodiumCard({ row }) {
  const accent = RANK_ACCENT[row.rank] || { medal: "", ring: "ring-rf-ink/10", bg: "bg-white" };
  const top = row.level || {};
  const displayName = row.display_name || row.username || row.name || "Anonymous Cook";
  return (
    <Link
      to={`/cook/${row.user_id}`}
      data-testid={`podium-${row.rank}`}
      className={`relative rounded-3xl ${accent.bg} ring-4 ${accent.ring} p-6 flex flex-col items-center text-center hover:scale-[1.02] transition-transform`}
    >
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-4xl">{accent.medal}</div>
      <div className="w-20 h-20 rounded-full bg-white/80 flex items-center justify-center text-4xl font-black text-rf-ink mb-3 shadow-inner">
        {displayName.charAt(0).toUpperCase()}
      </div>
      <p className="font-display font-black text-xl text-rf-ink leading-tight">@{displayName}</p>
      <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold mt-1">
        Lvl {top.level} · {top.name}
      </p>
      {row.current_streak >= 3 && (
        <p className="text-xs font-bold text-rf-orange mt-2 inline-flex items-center gap-1">
          <Fire size={12} weight="fill" /> {row.current_streak}-day streak
        </p>
      )}
      <div className="mt-4 flex items-center gap-4 text-sm">
        <div>
          <p className="font-black text-2xl text-rf-orange leading-none">{row.recipes_in_period}</p>
          <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold">This period</p>
        </div>
        <div className="w-px h-8 bg-rf-ink/10" />
        <div>
          <p className="font-black text-2xl text-rf-ink leading-none">{row.total_public_recipes}</p>
          <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold">Lifetime</p>
        </div>
      </div>
    </Link>
  );
}

function LeaderRow({ row }) {
  const top = row.level || {};
  const displayName = row.display_name || row.username || row.name || "Anonymous Cook";
  return (
    <Link
      to={`/cook/${row.user_id}`}
      data-testid={`leader-row-${row.rank}`}
      className="flex items-center gap-4 py-4 px-5 rounded-2xl bg-white border border-rf-ink/10 hover:border-rf-orange/60 transition-colors"
    >
      <div className="w-10 text-center">
        <span className="font-display font-black text-xl text-rf-ink-soft">#{row.rank}</span>
      </div>
      <div className="w-12 h-12 rounded-full bg-rf-amber/30 flex items-center justify-center font-black text-lg text-rf-ink">
        {displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-rf-ink truncate">@{displayName}</p>
        <p className="text-xs text-rf-ink-soft">
          Lvl {top.level} · {top.name}
          {row.current_streak >= 3 && (
            <span className="ml-2 text-rf-orange inline-flex items-center gap-1">
              · <Fire size={10} weight="fill" /> {row.current_streak}-day streak
            </span>
          )}
        </p>
      </div>
      <div className="text-right">
        <p className="font-black text-lg text-rf-orange leading-none">{row.recipes_in_period}</p>
        <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold">
          {row.recipes_in_period === 1 ? "recipe" : "recipes"}
        </p>
      </div>
    </Link>
  );
}

export default function Leaderboard() {
  const { flags, loading: flagsLoading } = useSiteFlags();
  const { user } = useAuth();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.gamification_enabled && !isStaff) return;
    setLoading(true);
    api
      .get(`/gamification/leaderboard?period=${period}&limit=25`)
      .then((r) => setRows(r.data.leaders || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period, flags.gamification_enabled, flagsLoading, isStaff]);

  // Feature-flag gating — mirror the backend behaviour. Non-staff visitors
  // see a friendly "coming soon" placeholder instead of a 404, so nothing
  // breaks if the link somehow leaks (e.g. Google indexed a preview URL).
  if (!flagsLoading && !flags.gamification_enabled && !isStaff) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <PageMeta title="Community leaderboard coming soon" noindex />
        <Trophy size={56} className="mx-auto text-rf-amber mb-4" weight="duotone" />
        <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Something delicious is cooking.</h1>
        <p className="text-rf-ink-soft mb-6">
          The community leaderboard isn&apos;t open just yet. Keep sharing recipes to the directory — your slot on the podium is being seasoned.
        </p>
        <Link to="/directory" className="btn-amber">Browse the directory</Link>
      </div>
    );
  }

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="leaderboard"
        fallbackTitle="Top Recipe Contributors — The Recipe Filer Leaderboard"
        fallbackDescription="See the home cooks contributing the most public recipes — all-time, this month and this week. Level up, earn badges and become a Star Contributor."
      />

      <p className="overline mb-4">Community Leaderboard</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-4 leading-[0.95]">
        Star Recipe<br />Contributors.
      </h1>
      <p className="text-lg text-rf-ink-soft max-w-2xl mb-10">
        Cooks who share the most public recipes climb the ranks, earn category badges, and unlock chef levels. Bragging rights, no fees.
      </p>

      {isStaff && !flags.gamification_enabled && (
        <div data-testid="leaderboard-staff-banner" className="mb-8 p-4 rounded-2xl border border-rf-amber bg-rf-amber/15 text-rf-ink text-sm">
          <b>Staff preview:</b> gamification is currently OFF for the public. Toggle it in Admin → Site Config when you&apos;re ready to launch.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-10 items-center">
        <div className="flex bg-white rounded-full p-1 border border-rf-ink/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = period === t.id;
            return (
              <button
                key={t.id}
                data-testid={`leaderboard-tab-${t.id}`}
                onClick={() => setPeriod(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-colors ${
                  active ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft hover:text-rf-ink"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "duotone"} />
                {t.label}
              </button>
            );
          })}
        </div>
        <Link
          to="/directory"
          data-testid="leaderboard-directory-cta"
          className="text-sm font-bold text-rf-ink-soft hover:text-rf-orange"
        >
          Browse the directory →
        </Link>
      </div>

      {loading && (
        <p data-testid="leaderboard-loading" className="text-rf-ink-soft">Tallying up the cooks…</p>
      )}

      {!loading && rows.length === 0 && (
        <div data-testid="leaderboard-empty" className="rounded-3xl bg-white border border-rf-ink/10 p-10 text-center">
          <ChefHat size={48} className="mx-auto text-rf-amber mb-4" weight="duotone" />
          <p className="font-display text-2xl font-black text-rf-ink mb-2">Nobody&apos;s on the board yet.</p>
          <p className="text-rf-ink-soft mb-6">Be the first to share a public recipe this period.</p>
          <Link to="/scan" className="btn-amber">Share a recipe</Link>
        </div>
      )}

      {!loading && podium.length > 0 && (
        <div data-testid="leaderboard-podium" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {podium.map((row) => (
            <PodiumCard key={row.user_id} row={row} />
          ))}
        </div>
      )}

      {!loading && rest.length > 0 && (
        <div data-testid="leaderboard-list" className="flex flex-col gap-3">
          {rest.map((row) => (
            <LeaderRow key={row.user_id} row={row} />
          ))}
        </div>
      )}

      <div className="mt-16 p-8 rounded-3xl bg-rf-ink text-rf-cream">
        <h2 className="font-display text-3xl font-black mb-3">How do I climb the board?</h2>
        <ul className="space-y-2 text-rf-cream/85">
          <li>· Every public recipe you share earns <b>10 XP</b>.</li>
          <li>· Sharing across new tags earns bonus <b>diversity XP</b>.</li>
          <li>· Weekly & monthly boards reset — great chance for new cooks to shine.</li>
          <li>· Category masters (bread, cakes, salads, grill…) unlock at 5 recipes per category.</li>
        </ul>
        <div className="mt-6 flex gap-3">
          <Link to="/scan" data-testid="leaderboard-share-cta" className="btn-amber !bg-rf-amber !text-rf-ink">
            Share a recipe
          </Link>
          <Link to="/directory" className="btn-outline-ink !border-rf-cream !text-rf-cream">
            Browse the directory
          </Link>
        </div>
      </div>
    </div>
  );
}
